import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
    PanelPosition,
    panelPositionBesideToolbar,
    panelPositionMovedBy,
    savedPanelPositionOrDefault,
} from '~/components/toolbar/shared/floatingPanelPlacement';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { bulkRoiSelectionActiveState } from '~/state/atoms/structureSet/roiAtoms';
import { operationPanelState } from '~/state/atoms/toolsAtoms';
import UnifiedOperationPanel, {
    OPERATION_PANEL_WIDTH,
} from './UnifiedOperationPanel';

interface OperationsPanelHandlerProps {
    toolbarRef: RefObject<HTMLElement | null>;
}

export default function OperationsPanelHandler({
    toolbarRef,
}: OperationsPanelHandlerProps) {
    const [panelState, setPanelState] = useRecoilState(operationPanelState);
    const { roiNumber: selectedROI } = useActiveEditTarget();
    const isBulkSelectionActive = useRecoilValue(bulkRoiSelectionActiveState);
    const isOpen = panelState.type !== 'closed';

    const [position, setPosition] = useState<PanelPosition | null>(null);

    const lastPositionRef = useRef<PanelPosition | null>(null);

    const getDefaultPosition = useCallback((): PanelPosition | null => {
        const anchor = toolbarRef.current;
        if (!anchor) {
            return null;
        }
        return panelPositionBesideToolbar(
            anchor.getBoundingClientRect(),
            OPERATION_PANEL_WIDTH
        );
    }, [toolbarRef]);

    // Restore saved position or compute default when opening
    const prevTypeRef = useRef(panelState.type);
    useEffect(() => {
        const wasClosed = prevTypeRef.current === 'closed';
        const isNowOpen = panelState.type !== 'closed';
        if (wasClosed && isNowOpen) {
            const pos = savedPanelPositionOrDefault(
                lastPositionRef.current,
                OPERATION_PANEL_WIDTH,
                getDefaultPosition
            );
            setPosition(pos);
            lastPositionRef.current = pos;
        }
        prevTypeRef.current = panelState.type;
    }, [panelState.type, getDefaultPosition]);

    // Auto-close margin/ring panel when no ROI is selected
    useEffect(() => {
        if (
            (selectedROI < 0 || isBulkSelectionActive) &&
            (panelState.type === 'margin' || panelState.type === 'ring')
        ) {
            setPanelState({ type: 'closed' });
        }
    }, [selectedROI, isBulkSelectionActive, panelState.type, setPanelState]);

    const handleClose = useCallback(() => {
        setPanelState({ type: 'closed' });
    }, [setPanelState]);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        setPosition((prev) => {
            if (!prev) {
                return prev;
            }
            const next = panelPositionMovedBy(prev, event.delta);
            lastPositionRef.current = next;
            return next;
        });
    }, []);

    if (!isOpen) {
        return null;
    }

    return createPortal(
        <DndContext onDragEnd={handleDragEnd}>
            <UnifiedOperationPanel
                panelState={panelState}
                onClose={handleClose}
                position={position}
            />
        </DndContext>,
        document.body
    );
}
